// Case handlers for validation functions.
export const onValidateCases = ["default", "negated", "no-closed-parentheses", "variant-expression"] as const;
export type OnValidateCase = (typeof onValidateCases)[number];

// Expression boolean values.
const EXPR_TRUE = "$true";
const EXPR_FALSE = "$false";

export type FeatureSwitchValidationFunc = (type: OnValidateCase, fs: string, variant?: string) => void;
export interface EvaluationConfig {
  isFeatureSwitchEnabled: (fs: string) => boolean;
  isFeatureSwitchVariantEnabled: (fs: string, variant?: string) => boolean;
  validate?: FeatureSwitchValidationFunc;
}

/** Evaluates expressions of the form `fs1 = v1 & fs2 | fs1 != v3 & !fs3`
 * for feature switches and variants, including support for parentheses.
 * If no = equality comparison with a variant is specified,
 *  one of the "true" variants is implied (as defined in `FS_ENABLED_VARIANTS`).
 * Expression must be in canonical form, & having higher precedence than |. */
export function evaluateFeatureSwitchCondition(expr: string, config: EvaluationConfig): boolean {
  // Stack-based approach to handle parentheses.
  const stack: string[] = [];
  let currentExpr = "";
  let openParentheses = 0;

  for (const char of expr) {
    if (char === "(") {
      stack.push(currentExpr);
      openParentheses++;
      currentExpr = "";
    } else if (char === ")") {
      openParentheses--;
      const result = evaluateSimpleExpression(currentExpr, config);
      currentExpr = stack.pop() + (result ? EXPR_TRUE : EXPR_FALSE);
    } else {
      currentExpr += char;
    }
  }

  if (openParentheses !== 0) {
    config.validate?.("no-closed-parentheses", currentExpr);
    return false;
  }

  return evaluateSimpleExpression(currentExpr, config);
}

/** Helper function for evaluating the simple, non-parenthesized expressions. */
function evaluateSimpleExpression(expr: string, config: EvaluationConfig): boolean {
  const ds = expr.split("|");
  // If this loop is allowed to complete its tests, return true.
  // Otherwise, the loop will break when `ds` is exhausted.
  disj: for (const d of ds) {
    const cs = d.split("&");
    for (const c0 of cs) {
      const c = c0.trim();

      if (c === EXPR_FALSE) continue disj;
      if (c === EXPR_TRUE) continue;

      // Negated test.
      if (c.startsWith("!")) {
        const fs = c.substring(1);
        config.validate?.("negated", fs);
        if (config.isFeatureSwitchEnabled(fs)) {
          continue disj;
        }
      } else {
        // Get test expectations.
        let expectation: boolean | undefined;
        let fsNameVar = c.split("!=");
        if (fsNameVar.length === 2) {
          expectation = false;
        } else {
          fsNameVar = c.split("=");
          if (fsNameVar.length === 2) {
            expectation = true;
          }
        }

        // Default evaluation.
        if (expectation === undefined) {
          config.validate?.("default", c);
          if (!config.isFeatureSwitchEnabled(c)) {
            continue disj;
          }
          // Evaluate variant test expectations.
        } else {
          const fsName = fsNameVar[0].trim();
          const fsVar = fsNameVar[1].trim();
          config.validate?.("variant-expression", fsName, fsVar);
          if (config.isFeatureSwitchVariantEnabled(fsName, fsVar) !== expectation) {
            continue disj;
          }
        }
      }
    }
    return true;
  }
  return false;
}
